// src/components/analyst/analystAccess.ts
//
// The one place the analyst UI decides who may do what (plan
// 2026-10-24-whatsapp-daily-summary § D8). It mirrors the backend's
// `services/periodic_analysis/access.py` + `ROUTE_ACCESS`:
//
//   - READ (the feed, a run, its evidence, Metrics, Open, your own
//     subscription): account OWNER, ADMIN and MEMBER. A MEMBER sees only the
//     analyses of workspaces they belong to; the backend filters, the UI does
//     not have to.
//   - MANAGE (schedules, run now, instructions, evidence feedback, everyone's
//     subscriptions, the follow-up conversation): OWNER and ADMIN, plus a
//     superadmin.
//   - VIEWER and SUPPORT get neither.
//
// Hiding a control is presentation: the backend refuses the request on its
// own (403 for MANAGE, 404 for an analysis outside a MEMBER's workspaces). The
// point of gating here is that the page never offers a door the API slams, and
// never fires a request on load that a MEMBER can only get a 403 for.

import { useAuth } from "@/context/AuthContext";
import type { AccountRole } from "@/types/auth";

/** Account roles that may open the Analyst (nav item + every /analyst route). */
export const ANALYST_READER_ROLES: AccountRole[] = ["OWNER", "ADMIN", "MEMBER"];

/** Account roles that may manage the Analyst. A superadmin always may. */
const ANALYST_MANAGER_ROLES: readonly string[] = ["OWNER", "ADMIN"];

type AccountLike = { role?: string | null } | null | undefined;
type UserLike = { isSuperadmin?: boolean | null } | null | undefined;

/** Schedules, run now, instructions, feedback, everyone's subscriptions and
 *  the follow-up conversation: OWNER/ADMIN or a superadmin. */
export function canManageAnalyst(activeAccount: AccountLike, user: UserLike): boolean {
  if (user?.isSuperadmin) return true;
  const role = activeAccount?.role;
  return !!role && ANALYST_MANAGER_ROLES.includes(role);
}

/** `canManageAnalyst` for the signed-in caller. */
export function useCanManageAnalyst(): boolean {
  const { activeAccount, user } = useAuth();
  return canManageAnalyst(activeAccount, user);
}
